import { useNavigate } from 'react-router-dom';
import { Lock, CheckCircle, ChevronRight } from 'lucide-react';
import { CourseLevel } from '../interfaces/course';

interface LevelsListProps {
  levels: CourseLevel[];
  courseId: string;
}

export default function LevelsList({ levels, courseId }: LevelsListProps) {
  const navigate = useNavigate();

  const handleLevelClick = (level: CourseLevel, index: number) => {
    const isLocked = index > 0 && !levels[index - 1].completed_at;

    if (!isLocked) {
      navigate(`/courses/${courseId}/levels/${level.id}`);
    }
  };

  // Helper function to check if a level is completed
  const isLevelCompleted = (level: CourseLevel) => {
    return level.completed_at !== null;
  };

  // Helper function to check if a level is locked
  const isLevelLocked = (index: number) => {
    if (index === 0) return false;
    return !isLevelCompleted(levels[index - 1]);
  };

  return (
    <div className="space-y-5">
      {levels.map((level, index) => {
        const completed = isLevelCompleted(level);
        const locked = isLevelLocked(index);

        return (
          <div
            key={level.id}
            className={`relative rounded-3xl shadow-sm overflow-hidden transition-all duration-300 group ${
              locked ? 'pointer-events-none' : 'hover:shadow-md cursor-pointer'
            } bg-white`}
            onClick={() => handleLevelClick(level, index)}
          >
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-title">{level.name}</h3>
                  <p className="text-sm text-subheading mt-1">
                    {level.description}
                  </p>
                  <span className="text-xs text-subheading block mt-2">
                    {level.question_count} question
                    {level.question_count !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="ml-4">
                  {completed && (
                    <div className="w-10 h-10 rounded-full bg-bg-chat flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                  )}
                  {!completed && !locked && (
                    <div className="w-10 h-10 rounded-full bg-bg-chat flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <ChevronRight className="w-6 h-6 text-accent-purple" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {locked && (
              <div className="absolute inset-0 backdrop-blur-[1px] bg-white/40 flex items-center justify-center">
                <div className="text-center bg-white/90 py-3 px-6 rounded-xl shadow-sm">
                  <Lock className="w-6 h-6 text-subheading mx-auto mb-2" />
                  <p className="text-sm font-medium text-subheading">
                    Complete previous level
                  </p>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
